import React, { createContext, useContext, useReducer, ReactNode } from "react";
import { WizardState } from "./types";
import { isFirstStep, isLastStep } from "./predicates";

type WizardContextType = {
  state: WizardState;
  dispatch: React.Dispatch<any>;
};

const WizardContext = createContext<WizardContextType | undefined>(undefined);
const nextStep = (state: WizardState) => {
  let newIndex = state.stepIndex;
  if (!isLastStep(state)) {
    newIndex = state.stepIndex + 1;
  }
  const newState = {
    ...state,
    stepIndex: newIndex,
  };
  return {
    ...newState,
    step: state.steps[newIndex],
    lastValidStep:
      state.lastValidStep < newIndex ? newIndex : state.lastValidStep,
    previousBtn: {
      ...state.previousBtn,
      disabled: isFirstStep(newState),
    },
    nextBtn: {
      ...state.nextBtn,
      disabled: isLastStep(newState),
    },
  };
};
const wizardReducer = (state: WizardState, action: any): WizardState => {
  switch (action.type) {
    case "NEXT_STEP": {
      const newData = nextStep(state);
      return nextStep(state);
    }
    case "PREVIOUS_STEP":
      // Implement the logic for moving to the previous step
      const newIndexPrevious = state.stepIndex - 1;
      const newStatePrevious = {
        ...state,
        stepIndex: newIndexPrevious,
      };
      return {
        ...state,
        stepIndex: Math.max(newIndexPrevious, 0),
        step: state.steps[Math.max(newIndexPrevious, 0)],
        previousBtn: {
          ...state.previousBtn,
          disabled: isFirstStep(newStatePrevious),
        },
        nextBtn: {
          ...state.nextBtn,
          disabled: isLastStep(newStatePrevious),
        },
      };
    case "SET_STEP_DATA":
      // Implement the logic for setting step data
      return {
        ...state,
        steps: state.steps.map((step, index) =>
          index === action.payload.index
            ? { ...step, data: action.payload.data }
            : step
        ),
      };
    case "SUBMIT_FORM_STEP":
      // Implement the logic for setting step data
      const newState = {
        ...nextStep(state),
        steps: state.steps.map((step, index) =>
          index === state.stepIndex ? { ...step, data: action.payload } : step
        ),
      };
      return newState;
    case "FINISH":
      // Implement the logic for setting step data
      return {
        ...state,
        steps: state.steps.map((step, index) =>
          index === action.payload.index
            ? { ...step, data: action.payload.data }
            : step
        ),
      };
    default:
      return state;
  }
};

const WizardProvider = ({
  children,
  initialState,
}: {
  children: ReactNode;
  initialState: WizardState;
}) => {
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  return (
    <WizardContext.Provider value={{ state, dispatch }}>
      {children}
    </WizardContext.Provider>
  );
};

const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error("useWizardContext must be used within a WizardProvider");
  }
  return context;
};

export { WizardProvider, useWizard };
